/**
 * Cloud Accounts Service
 * OAuth connect/disconnect of the user's personal cloud (Drive/Dropbox/OneDrive)
 * via the export-worker. Used by the Cloud Sync feature.
 */

const EXPORT_WORKER_URL =
  import.meta.env.VITE_EXPORT_WORKER_URL || "https://export.masterlapse.com";

export type CloudProviderId = "google" | "dropbox" | "onedrive";

export interface CloudAccount {
  provider: CloudProviderId;
  email: string;
  status: "connected" | "revoked";
  connectedAt?: unknown; // Firestore Timestamp
  /** Storage quota cache — written at connect and refreshed by the worker. */
  quota?: { totalBytes: number | null; usedBytes: number; fetchedAt: string };
  /** Web URL of the MasterLapse root folder on this cloud. */
  rootFolderUrl?: string;
}

export const PROVIDER_LABELS: Record<CloudProviderId, string> = {
  google: "Google Drive",
  dropbox: "Dropbox",
  onedrive: "OneDrive",
};

// OneDrive (requires an Azure tenant) is a future phase — re-adding a
// provider here is the only dashboard change needed.
export const ENABLED_PROVIDERS: CloudProviderId[] = ["google", "dropbox"];

/** POST to the export-worker with the Firebase ID token. Shared with syncService. */
export async function workerPost<T>(path: string, idToken: string, body: unknown): Promise<T> {
  const response = await fetch(`${EXPORT_WORKER_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error((errorData as { error: string }).error || `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

/**
 * Begin the OAuth connect flow for a provider.
 * Returns the provider authorize URL to redirect the browser to.
 */
export async function getOAuthStartUrl(
  provider: CloudProviderId,
  idToken: string
): Promise<string> {
  const { url } = await workerPost<{ url: string }>("/oauth/start", idToken, { provider });
  return url;
}

/** Disconnect a provider (revokes the token server-side and deletes the account docs). */
export async function disconnectAccount(
  provider: CloudProviderId,
  idToken: string
): Promise<void> {
  await workerPost<{ success: boolean }>("/accounts/disconnect", idToken, { provider });
}
